import { set, cloneDeep } from 'lodash';
import type { JSX } from 'react';

import {
  type FieldNamePickerConfigSettings,
  type NumberFieldConfigSettings,
  type SelectFieldConfigSettings,
  type SliderFieldConfigSettings,
  type StringFieldConfigSettings,
  type UnitFieldConfigSettings,
  booleanOverrideProcessor,
  identityOverrideProcessor,
  numberOverrideProcessor,
  selectOverrideProcessor,
  stringOverrideProcessor,
  unitOverrideProcessor,
} from '../field/overrides/processors';
import {
  type StandardEditorContext,
  type StandardEditorProps,
  standardEditorsRegistry,
} from '../field/standardFieldConfigEditorRegistry';
import { type PanelOptionsSupplier } from '../panel/PanelPlugin';
import { type OptionsEditorItem, OptionsUIRegistryBuilder } from '../types/OptionsUIRegistryBuilder';
import { isObject } from '../types/data';
import { type FieldConfigPropertyItem, type FieldConfigEditorConfig } from '../types/fieldOverrides';
import { type PanelOptionsEditorConfig, type PanelOptionsEditorItem } from '../types/panel';

/**
 * Fluent API for declarative creation of field config option editors
 */
export class FieldConfigEditorBuilder<TOptions, TContextOptions = unknown> extends OptionsUIRegistryBuilder<
  TOptions,
  StandardEditorProps,
  // The item type has to carry TContextOptions too: the base class intersects it with the config
  // type, and if only one side is typed the context degrades. The `any`s keep the item permissive so each add*
  // method below can narrow value and settings, as before.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  FieldConfigPropertyItem<TOptions, any, any, TContextOptions>,
  TContextOptions
> {
  addNumberInput<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & NumberFieldConfigSettings, number, TContextOptions>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      override: standardEditorsRegistry.get('number').editor,
      editor: standardEditorsRegistry.get('number').editor,
      process: numberOverrideProcessor,
      shouldApply: config.shouldApply ?? (() => true),
      settings: config.settings || {},
    });
  }

  addSliderInput<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & SliderFieldConfigSettings, number, TContextOptions>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      override: standardEditorsRegistry.get('slider').editor,
      editor: standardEditorsRegistry.get('slider').editor,
      process: numberOverrideProcessor,
      shouldApply: config.shouldApply ?? (() => true),
      settings: config.settings || {},
    });
  }

  addTextInput<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & StringFieldConfigSettings, string, TContextOptions>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      override: standardEditorsRegistry.get('text').editor,
      editor: standardEditorsRegistry.get('text').editor,
      process: stringOverrideProcessor,
      shouldApply: config.shouldApply ?? (() => true),
      settings: config.settings || {},
    });
  }

  addSelect<TOption, TSettings extends SelectFieldConfigSettings<TOption>>(
    config: FieldConfigEditorConfig<TOptions, TSettings, TOption, TContextOptions>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      override: standardEditorsRegistry.get('select').editor,
      editor: standardEditorsRegistry.get('select').editor,
      process: selectOverrideProcessor,
      // ???
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || { options: [] },
    });
  }

  addRadio<TOption, TSettings>(config: FieldConfigEditorConfig<TOptions, TSettings, TOption, TContextOptions>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      override: standardEditorsRegistry.get('radio').editor,
      editor: standardEditorsRegistry.get('radio').editor,
      useFieldset: true,
      process: selectOverrideProcessor,
      // ???
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || { options: [] },
    });
  }

  addBooleanSwitch<TSettings>(config: FieldConfigEditorConfig<TOptions, TSettings, boolean, TContextOptions>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('boolean').editor,
      override: standardEditorsRegistry.get('boolean').editor,
      process: booleanOverrideProcessor,
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || {},
    });
  }

  addColorPicker<TSettings>(config: FieldConfigEditorConfig<TOptions, TSettings, string, TContextOptions>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('color').editor,
      override: standardEditorsRegistry.get('color').editor,
      process: identityOverrideProcessor,
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || {},
    });
  }

  addUnitPicker<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & UnitFieldConfigSettings, string, TContextOptions>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('unit').editor,
      override: standardEditorsRegistry.get('unit').editor,
      process: unitOverrideProcessor,
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || {},
    });
  }

  addFieldNamePicker<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & FieldNamePickerConfigSettings, string, TContextOptions>
  ): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('field-name').editor,
      override: standardEditorsRegistry.get('field-name').editor,
      process: identityOverrideProcessor,
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || {},
    });
  }

  addGenericEditor<TSettings>(
    config: FieldConfigEditorConfig<TOptions, TSettings & any, unknown, TContextOptions>, // & any... i give up!
    editor: (props: StandardEditorProps<TSettings>) => JSX.Element
  ): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: editor,
      override: editor,
      process: identityOverrideProcessor,
      shouldApply: config.shouldApply ? config.shouldApply : () => true,
      settings: config.settings || {},
    });
  }
}

/**
 * Provides read and write access to a specific path within a parent options object.
 * Used as the value accessor when building nested panel option editors via
 * {@link PanelOptionsEditorBuilder.addNestedOptions}.
 */
export interface NestedValueAccess {
  getValue: (path: string) => any;
  onChange: (path: string, value: any) => void;
  getContext?: (parent: StandardEditorContext<any>) => StandardEditorContext<any>;
}

/**
 * Configuration for a nested sub-section of panel options, used with
 * {@link PanelOptionsEditorBuilder.addNestedOptions}.
 *
 * @typeParam TSub - The type of the nested options object.
 */
export interface NestedPanelOptions<TSub = any> {
  /** The dot-separated path within the parent options object where the sub-options live. */
  path: string;
  /** Optional category label(s) for grouping in the options pane. */
  category?: string[];
  /** Default value for the sub-options object. */
  defaultValue?: TSub;
  /** Builder function that declares the editors for the sub-options. */
  build: PanelOptionsSupplier<TSub>;
  /** Optional override for how values are read/written relative to the parent accessor. */
  values?: (parent: NestedValueAccess) => NestedValueAccess;
}

class NestedPanelOptionsBuilder<TSub = any> implements OptionsEditorItem<TSub, any, any, any> {
  path = '';
  category?: string[];
  defaultValue?: TSub;
  id = 'nested-panel-options';
  name = 'nested';
  editor = () => null;

  constructor(public cfg: NestedPanelOptions<TSub>) {
    this.path = cfg.path;
    this.category = cfg.category;
    this.defaultValue = this.getDefaultValue(cfg);
  }

  private getDefaultValue(cfg: NestedPanelOptions<TSub>): TSub {
    let result = isObject(cfg.defaultValue) ? cloneDeep(cfg.defaultValue) : {};

    const builder = new PanelOptionsEditorBuilder<TSub>();
    cfg.build(builder, { data: [] });

    for (const item of builder.getItems()) {
      if (item.defaultValue != null) {
        set(result, item.path, item.defaultValue);
      }
    }

    // TSub is defined as type any and we need to cast it back
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return result as TSub;
  }

  getBuilder = () => {
    return this.cfg.build;
  };

  getNestedValueAccess = (parent: NestedValueAccess): NestedValueAccess => {
    const values = this.cfg.values;
    if (values) {
      return values(parent);
    }
    // by default prefix the path
    return {
      getValue: (path: string) => parent.getValue(`${this.path}.${path}`),
      onChange: (path: string, value: unknown) => parent.onChange(`${this.path}.${path}`, value),
    };
  };
}

/**
 * Type guard that returns true if `item` is a {@link NestedPanelOptions} builder instance.
 * Useful when iterating over registered panel option items to identify and handle nested sub-options.
 */
export function isNestedPanelOptions(item: unknown): item is NestedPanelOptionsBuilder {
  return isObject(item) && 'id' in item && item.id === 'nested-panel-options';
}

/**
 * Fluent API for declarative creation of panel options
 */
export class PanelOptionsEditorBuilder<TOptions> extends OptionsUIRegistryBuilder<
  TOptions,
  StandardEditorProps,
  PanelOptionsEditorItem<TOptions>,
  TOptions
> {
  addNestedOptions<Sub>(opts: NestedPanelOptions<Sub>) {
    const s = new NestedPanelOptionsBuilder<Sub>(opts);
    return this.addCustomEditor(s);
  }

  addNumberInput<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings & NumberFieldConfigSettings, number>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('number').editor,
    });
  }

  addSliderInput<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings & SliderFieldConfigSettings, number>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('slider').editor,
    });
  }

  addTextInput<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings & StringFieldConfigSettings, string>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('text').editor,
    });
  }

  addStringArray<TSettings>(
    config: PanelOptionsEditorConfig<TOptions, TSettings & StringFieldConfigSettings, string[]>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('strings').editor,
    });
  }

  addSelect<TOption, TSettings extends SelectFieldConfigSettings<TOption>>(
    config: PanelOptionsEditorConfig<TOptions, TSettings, TOption>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('select').editor,
    });
  }

  addMultiSelect<TOption, TSettings extends SelectFieldConfigSettings<TOption>>(
    config: PanelOptionsEditorConfig<TOptions, TSettings, TOption>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('multi-select').editor,
    });
  }

  addRadio<TOption, TSettings extends SelectFieldConfigSettings<TOption>>(
    config: PanelOptionsEditorConfig<TOptions, TSettings, TOption>
  ) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      useFieldset: true,
      editor: standardEditorsRegistry.get('radio').editor,
    });
  }

  addBooleanSwitch<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings, boolean>) {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('boolean').editor,
    });
  }

  addColorPicker<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings, string>): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('color').editor,
      settings: config.settings || {},
    });
  }

  addTimeZonePicker<TSettings>(config: PanelOptionsEditorConfig<TOptions, TSettings, string>): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('timezone').editor,
      settings: config.settings || {},
    });
  }

  addUnitPicker<TSettings>(
    config: PanelOptionsEditorConfig<TOptions, TSettings & UnitFieldConfigSettings, string>
  ): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('unit').editor,
    });
  }

  addFieldNamePicker<TSettings>(
    config: PanelOptionsEditorConfig<TOptions, TSettings & FieldNamePickerConfigSettings, string>
  ): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('field-name').editor,
    });
  }

  addDashboardPicker<TSettings>(
    config: PanelOptionsEditorConfig<TOptions, TSettings & FieldNamePickerConfigSettings, string>
  ): this {
    return this.addCustomEditor({
      ...config,
      id: config.path,
      editor: standardEditorsRegistry.get('dashboard-uid').editor, // added at runtime
    });
  }
}
